// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import * as Immutable from 'immutable';

// Utils
import { getAllCellsBetween } from '../../../../../../element/table/legacy/utils/tableCellSelectionUtils';
import {
    getElementId,
    getTableContentCellTextStyle,
} from '../../../../../../../common/elements/utils/elementPropertyUtils';
import { getElementDefaultColors } from '../../editorTools/textStylePopup/textStylePopupUtils';

// Components
import DraftTextColorButton from './DraftTextColorButton';
import TextStylePopupContent from '../../editorTools/textStylePopup/TextStylePopupContent';

// Constants
import {
    TEXT_BACKGROUND_PRESETS,
    TEXT_COLOR_PRESETS,
} from '../../../../../../components/editor/plugins/textColor/textColorConstants';
import {
    useLegacyActiveTableCallback,
    useLegacyActiveTableSelector,
} from '../../../../../../element/table/store/legacyHotTableStoreHooks';
import { selectLegacyActiveTableCellSelection } from '../../../../../../element/table/store/selectors/legacyActiveHotTableSelectors';
import { updateTableTextStyle } from '../../../../../../element/table/legacy/actions/hotTableActions';

const getCurrentStyle = (currentCellSelections, element) => {
    const selectedCellsCoords = getAllCellsBetween(currentCellSelections);
    const firstSelectedCellCoords = selectedCellsCoords.shift();

    if (!firstSelectedCellCoords) return Immutable.OrderedSet();

    const firstSelectedCellTextStyles = Immutable.OrderedSet(
        getTableContentCellTextStyle(firstSelectedCellCoords)(element),
    );

    return selectedCellsCoords.reduce(
        (acc, cellCoords) => acc.intersect(getTableContentCellTextStyle(cellCoords)(element)),
        firstSelectedCellTextStyles,
    );
};

const LegacyTextStyleRichTextToolPopup = ({ showBlockStyleTools, ...rest }) => {
    const { element } = rest;
    const { defaultTextColor, defaultBackgroundColor } = getElementDefaultColors(element) || {};

    const currentCellSelections = useLegacyActiveTableSelector(selectLegacyActiveTableCellSelection);
    const currentStyle = getCurrentStyle(currentCellSelections, element);

    const elementId = getElementId(element);

    const dispatch = useDispatch();
    const onTextColorClick = useLegacyActiveTableCallback(
        (activeTable, textStyle) =>
            dispatch(updateTableTextStyle({ hotInstance: activeTable, elementId, textStyle })),
        [elementId],
    );

    return (
        <TextStylePopupContent
            className="LegacyTextStyleRichTextToolPopup"
            textColorComponents={[
                <DraftTextColorButton
                    key="default"
                    {...rest}
                    preset={null}
                    onTextColorClick={onTextColorClick}
                    currentStyle={currentStyle}
                    customTextColor={defaultTextColor}
                    customBackgroundColor={defaultBackgroundColor}
                />,
                ...Object.values(TEXT_COLOR_PRESETS).map((preset) => (
                    <DraftTextColorButton
                        key={preset}
                        {...rest}
                        preset={preset}
                        onTextColorClick={onTextColorClick}
                        currentStyle={currentStyle}
                        customBackgroundColor={defaultBackgroundColor}
                    />
                )),
            ]}
            textHighlightComponents={Object.values(TEXT_BACKGROUND_PRESETS).map((preset) => (
                <DraftTextColorButton
                    key={preset}
                    {...rest}
                    preset={preset}
                    onTextColorClick={onTextColorClick}
                    currentStyle={currentStyle}
                />
            ))}
        />
    );
};

LegacyTextStyleRichTextToolPopup.propTypes = {
    showBlockStyleTools: PropTypes.bool,
    element: PropTypes.object,
};

export default LegacyTextStyleRichTextToolPopup;
